import * as DialogPrimitive from '@radix-ui/react-dialog'
import {XIcon} from 'lucide-react'
import * as React from 'react'
import {cn} from '../utils'

function Dialog({...props}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({...props}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({...props}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({...props}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 h-screen bg-black/50',
      className,
    )}
    {...props}
  />
))

function DialogContent({
  className,
  children,
  showCloseButton = true,
  contentClassName,
  overlayClassName,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  contentClassName?: string
  overlayClassName?: string
  style?: React.CSSProperties
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lg shadow-lg duration-200',
          // Default sizing only if no custom className is provided
          !className &&
            'max-h-[100dvh] max-h-[calc(var(--vvh,1vh)*100)] w-full max-w-xl sm:max-h-[calc(100dvh-4rem)] sm:max-h-[calc(var(--vvh,1vh)*100-4rem)]',
          className,
        )}
        style={style}
        {...props}
      >
        <div
          className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-auto overscroll-contain p-5', contentClassName)}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="absolute top-4 right-4 rounded-xs text-gray-500 transition-all hover:text-gray-700 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none dark:text-gray-400 dark:hover:text-gray-200 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

/**
 * Right-side panel variant of {@link DialogContent}: a full-height panel
 * pinned to the right edge over a dimmed, blurred backdrop.
 */
function DialogSideContent({
  className,
  children,
  contentClassName,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  contentClassName?: string
  style?: React.CSSProperties
}) {
  return (
    <DialogPortal data-slot="dialog-side-portal">
      <DialogOverlay className="backdrop-blur-sm" />
      <DialogPrimitive.Content
        data-slot="dialog-side-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-8 fixed inset-y-0 right-0 z-50 flex w-full max-w-[440px] flex-col overflow-hidden shadow-xl duration-200',
          className,
        )}
        style={style}
        {...props}
      >
        <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', contentClassName)}>{children}</div>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function DialogTitle({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({className, ...props}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogSideContent,
  DialogTitle,
  DialogTrigger,
}
